import { useAuth } from '@/context/AuthContext';
import { usePropertyFilter } from '@/utils/usePropertyFilter';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '@/styles/ActionItemsStyles.scss';
import '@/styles/PropertyStyles.scss';
import '@/styles/ActionItemsSwimlanes.scss';
import { toast } from 'react-toastify';
import {
    DndContext,
    DragOverlay,
    pointerWithin,
    rectIntersection,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable
} from '@dnd-kit/core';
import * as XLSX from 'xlsx';
import { FaBars } from 'react-icons/fa';

// Import extracted components
import KanbanColumn from './components/KanbanColumn';
import ActionItemsListView from './components/ActionItemsListView';
import SearchAndFilterBar from './components/SearchAndFilterBar';
import FilterPanel from './components/FilterPanel';
import MergeActionItemsModal from './components/MergeActionItemsModal';
import SuggestedMergesBanner from './components/SuggestedMergesBanner';
import RoutineChecksSwimlanes from './components/RoutineChecksSwimlanes';
import GroupBySwimlanes from './components/GroupBySwimlanes';
import CreateActionItemModal from './components/CreateActionItemModal';
import AutomationSchedulerChip from './components/AutomationSchedulerChip';

// Import board components
import BoardSidebar from './components/BoardSidebar';
import CreateBoardModal from './components/CreateBoardModal';
import BoardSettingsModal from './components/BoardSettingsModal';
import ManageAccessModal from './components/ManageAccessModal';
import DuplicateBoardModal from './components/DuplicateBoardModal';

// Import SignalR hooks for real-time updates
import useActionItemSignalR from '@/hooks/useActionItemSignalR';
import useActionItemBoardSignalR from '@/hooks/useActionItemBoardSignalR';

// Import board service
import * as kanbanBoardService from '@/services/kanbanBoardService';

// Session-persisted state hook — survives in-app navigation
import useSessionState from '@/hooks/useSessionState';
import * as Sentry from '@sentry/react';

// Status options used throughout the Action Items functionality
const STATUSES = ["Open", "On-hold", "Pending", "Solved", "Cancelled", "Closed"];

// Statuses shown when closed items are hidden - derived from STATUSES for consistency
const ACTIVE_STATUSES = STATUSES.filter(status => status !== 'Closed');

// Priority options used for action item priority selection
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];

// IT ticket types are now managed dynamically via the /api/ITTicketTypes endpoint.
// See ItTicketTypesPage.jsx for the admin UI.

// Polling interval in milliseconds - reduced since SignalR provides real-time updates
// This acts as a fallback to ensure data consistency if SignalR connection is lost
const POLLING_INTERVAL = 60000; // 60 seconds (reduced from 30s since SignalR handles real-time)

// Number of closed items to load at a time for pagination
const CLOSED_ITEMS_PAGE_SIZE = 10;

// Number of items to load per column per page
const COLUMN_PAGE_SIZE = 20;

// Debounce delay for search/filter changes (ms)
const SEARCH_DEBOUNCE_MS = 300;

/**
 * BoardsTabButton Component
 *
 * Renders the BOARDS sidebar toggle button as a droppable target using @dnd-kit/core's
 * `useDroppable`. This must be a separate component (not inlined in ActionItemsPage) so
 * that `useDroppable` is called inside the React tree of a `<DndContext>` provider —
 * `useDroppable` requires a DndContext ancestor or it will throw at runtime.
 *
 * @param {boolean} showBoardSidebar - Whether the sidebar is currently open (unused by
 *   the button itself but preserved for potential future styling decisions).
 * @param {Function} onToggle - Callback to toggle the sidebar open/closed on click.
 */
const BoardsTabButton = ({ onToggle }) => {
    // Register this element as a valid drop target with id "boards-tab-droppable".
    // The `type: 'boards-tab'` data is read by handleDragOver / handleDragEnd to detect
    // when a card is dragged over the BOARDS tab so the sidebar can auto-open.
    const { setNodeRef, isOver } = useDroppable({
        id: 'boards-tab-droppable',
        data: { type: 'boards-tab' }
    });

    return (
        <button
            ref={setNodeRef}
            className={`btn-toggle-sidebar ${isOver ? 'drag-hover' : ''}`}
            onClick={onToggle}
            title="Manage Boards"
            aria-label="Toggle boards sidebar"
        >
            <FaBars aria-hidden="true" />
            <span aria-hidden="true">BOARDS</span>
        </button>
    );
};

/**
 * ActionItemsPage Component
 * Displays action items in either Kanban or List view with filtering, search, and create functionality.
 * Templates are fetched from the API to dynamically render the create form.
 */
const ActionItemsPage = () => {
    const { apiFetch, user, hasPermission } = useAuth();
    const location = useLocation();

    // Property access filter — restricts the properties dropdown to only those
    // the current user is permitted to see. Unrestricted users see all properties.
    const { filterData: filterPropertiesByAccess } = usePropertyFilter();
    
    // Core data state
    const [actionItems, setActionItems] = useSessionState('ai_actionItems', []);
    const [propertiesMDA, setPropertiesMDA] = useState([]);
    const [allUsers, setAllUsers] = useState([]); // All users for assignee dropdown
    const [properties, setProperties] = useState([]);
    const [projects, setProjects] = useState([]); // Projects for linking project-based action items
    const [functions, setFunctions] = useState([]); // Functions for function-based assignment and swimlanes
    const [departments, setDepartments] = useState([]); // Departments for department-based assignment
    const [itCategories, setItCategories] = useState([]); // IT ticket categories for ITSM
    
    // Templates state - fetched from API instead of hardcoded
    const [templates, setTemplates] = useState([]);
    const [, setLoadingTemplates] = useState(true);
    
    // Ad-Hoc template state - fetched from ModuleTemplateAssignments
    const [, setAdHocTemplate] = useState(null);
    const [, setLoadingAdHocTemplate] = useState(true);

    // Board creation type selector — which action item types this user can create
    const [boardCreationTypes, setBoardCreationTypes] = useState([]);
    const [selectedCreationType, setSelectedCreationType] = useState(null);
    const [loadingCreationTypes, setLoadingCreationTypes] = useState(true);
    
    // UI state — session-persisted so selections survive in-app navigation
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilters, setShowFilters] = useSessionState('ai_showFilters', false);
    // Unified view mode: 'kanban' | 'swimlanes' | 'list'
    const [viewMode, setViewMode] = useSessionState('ai_viewMode', 'kanban');
    // Swimlane group-by dimension: 'function' | 'property' | 'assignee' | 'type'
    const [swimGroupBy, setSwimGroupBy] = useSessionState('ai_swimGroupBy', 'function');
    const [showModal, setShowModal] = useState(false);

    // Auto-open create modal when navigated with openCreate state (e.g. from Endpoint Management).
    // setState calls are deferred into a setTimeout callback so they are not executed
    // synchronously in the effect body (which would violate react-hooks/set-state-in-effect
    // and trigger an immediate cascading render on the same tick as the effect).
    useEffect(() => {
        if (!location.state?.openCreate) return;

        const timer = setTimeout(() => {
            setShowModal(true);
            // If an IT type was specified, pre-select it once boardCreationTypes are loaded
            if (location.state.selectedType && boardCreationTypes.length > 0) {
                const match = boardCreationTypes.find(t =>
                    t.actionItemsType === location.state.selectedType ||
                    t.templateKey === location.state.selectedType
                );
                if (match) setSelectedCreationType(match);
            }
            // Clear navigation state so the modal doesn't re-open on back navigation
            window.history.replaceState({}, document.title);
        }, 0);

        return () => clearTimeout(timer);
    }, [location.state, boardCreationTypes]);

    // Multi-select
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [mergeTargetId, setMergeTargetId] = useState(null);
    const [isMerging, setIsMerging] = useState(false);

    // Advanced filter state — session-persisted
    const [filterStatus, setFilterStatus] = useSessionState('ai_filterStatus', "");
    const [filterPriority, setFilterPriority] = useSessionState('ai_filterPriority', "");
    const [filterProperty, setFilterProperty] = useSessionState('ai_filterProperty', "");
    const [filterAssignee, setFilterAssignee] = useSessionState('ai_filterAssignee', "me_or_observer_or_dept_or_created");
    const [filterCreator, setFilterCreator] = useSessionState('ai_filterCreator', "");
    const [filterSource, setFilterSource] = useSessionState('ai_filterSource', "");
    const [showClosedItems, setShowClosedItems] = useSessionState('ai_showClosedItems', false);

    // Date range filter — session-persisted
    const [filterDatePreset, setFilterDatePreset] = useSessionState('ai_filterDatePreset', "");
    const [filterDateFrom, setFilterDateFrom] = useSessionState('ai_filterDateFrom', "");
    const [filterDateTo, setFilterDateTo] = useSessionState('ai_filterDateTo', "");

    // Sort state — session-persisted
    const [sortField, setSortField] = useSessionState('ai_sortField', 'lastUpdated');
    const [sortDirection, setSortDirection] = useSessionState('ai_sortDirection', 'desc');

    // Per-column pagination metadata: { [statusValue]: { page, totalCount, hasMore, loading } }
    const [columnMeta, setColumnMeta] = useSessionState('ai_columnMeta', {});

    // Debounce timer ref for search/filter changes
    const searchDebounceRef = useRef(null);
    
    // Pagination state for Closed column
    const [closedItemsLimit, setClosedItemsLimit] = useState(CLOSED_ITEMS_PAGE_SIZE);

    // Board columns state — fetched from the selected board's configuration
    const [boardColumns, setBoardColumns] = useState([]);

    // Board management state — selectedBoardId is session-persisted
    const [boards, setBoards] = useState([]);
    const [selectedBoardId, setSelectedBoardId] = useSessionState('ai_selectedBoardId', null);
    const [_loadingBoards, setLoadingBoards] = useState(true);
    const [showCreateBoardModal, setShowCreateBoardModal] = useState(false);
    const [showBoardSettingsModal, setShowBoardSettingsModal] = useState(false);
    const [showManageAccessModal, setShowManageAccessModal] = useState(false);
    const [showDuplicateBoardModal, setShowDuplicateBoardModal] = useState(false);
    const [currentBoard, setCurrentBoard] = useState(null);
    const [showBoardSidebar, setShowBoardSidebar] = useState(false);
    
    // Drag and drop state for cross-board transfers
    const [draggedItemId, setDraggedItemId] = useState(null);
    const [draggedItemSnapshot, setDraggedItemSnapshot] = useState(null);
    const [previewBoardId, setPreviewBoardId] = useState(null);
    const [hoverTimeout, setHoverTimeout] = useState(null);
    const boardsTabHoverRef = useRef(null); // Debounced timer for opening sidebar on drag-over
    const autoOpenedSidebarRef = useRef(false); // Track if sidebar was auto-opened during drag
    const actionItemsRequestRef = useRef(0);
    const boardColumnsRequestRef = useRef(0);
    const selectedBoardIdRef = useRef(selectedBoardId);
    
    const navigate = useNavigate();
    const userID = user.userID;
    // Determine if current user is an administrator (controls visibility of admin-only controls)
    const IsAdmin = hasPermission('/admin/action-items');

    // Sensors for the drag and drop zones
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    /**
     * SignalR hook for real-time action item updates.
     * Handles status changes, assignment changes, and other updates in real-time
     * without requiring page refresh or polling.
     */
    const handleSignalRUpdate = useCallback((update) => {
        
        // Check for actionItemId (lowercase from SignalR)
        const actionItemId = update?.actionItemId || update?.ActionItemId;
        
        // Validate update has actionItemId
        if (!update || !actionItemId) {
            return;
        }
        
        // Update the specific action item in our local state
        setActionItems(prevItems => {
            // Ensure we're comparing IDs correctly (handle both number and string)
            const updateId = parseInt(actionItemId, 10);
            
            // Check if updateId is valid
            if (isNaN(updateId)) {
                return prevItems;
            }
            
            let itemFound = false;
            const updated = prevItems.map(item => {
                // Compare IDs as numbers to handle type mismatches
                if (parseInt(item.id, 10) === updateId) {
                    itemFound = true;
                    
                    // Get update type (handle both cases)
                    const updateType = update.Type || update.type;
                    const newStatus = update.NewStatus || update.newStatus;
                    const newAssignedUserId = update.NewAssignedUserId ?? update.newAssignedUserId;
                    const newPriority = update.Data?.NewPriority || update.data?.newPriority;
                    // PropertyChange carries NewPropertyID in the Data payload.
                    // Both PascalCase (server) and camelCase (normalized) keys are checked
                    // because SignalR may serialize either way depending on the client SDK.
                    const newPropertyID = update.Data?.NewPropertyID ?? update.data?.newPropertyID;
                    
                    // Apply the update based on type
                    if (updateType === 'StatusChange' && newStatus) {
                        // Create a new object to ensure React detects the change
                        return { ...item, status: newStatus };
                    }
                    if (updateType === 'AssignmentChange' && newAssignedUserId !== undefined) {
                        return { ...item, assignedToUserID: newAssignedUserId };
                    }
                    if (updateType === 'PriorityChange' && newPriority) {
                        return { ...item, priority: newPriority };
                    }
                    // Update the propertyID so the Kanban card immediately shows the
                    // correct property name without waiting for the 60-second polling cycle.
                    if (updateType === 'PropertyChange' && newPropertyID !== undefined) {
                        return { ...item, propertyID: newPropertyID };
                    }
                }
                return item;
            });
            
            if (itemFound) {
                // Return a new array to ensure React detects the change
                return [...updated];
            } else {
                return prevItems;
            }
        });
    }, []);

    // SignalR real-time updates for action items
    // Connection is handled internally by the hook
    // Note: Toast notifications are handled globally in ProtectedLayout
    // This hook only updates the UI state when on the Action Items page
    useActionItemSignalR({
        onActionItemUpdated: handleSignalRUpdate,
        onStatusChange: handleSignalRUpdate,
        onAssignmentChange: handleSignalRUpdate,
        // Don't show toasts here - they're handled globally in ProtectedLayout
        // Just update the state so the UI reflects changes when on this page
        onNeedsAttention: handleSignalRUpdate,
        onAssignedToYou: handleSignalRUpdate,
        enabled: true,
    });

    /**
     * Load all boards accessible to the current user.
     *
     * Admin fallback strategy:
     *   When the primary /api/KanbanBoards endpoint SUCCEEDS but returns an empty
     *   list, an admin user could be in a state where all boards have been transferred
     *   away from them.  In that case we attempt the admin/all-boards endpoint so they
     *   can still manage orphan boards.
     *
     *   Critically, the fallback is only attempted on a SUCCESSFUL (but empty) primary
     *   response — NOT on a primary endpoint error.  If we tried the fallback on errors,
     *   a server-side failure (e.g. missing database table) would cascade into a secondary
     *   403 from admin/all-boards (which requires the JWT Role claim to be "Admin", whereas
     *   the frontend IsAdmin flag is permission-based and may be true for non-Admin-role users).
     */
    const loadBoards = useCallback(async () => {
        try {
            setLoadingBoards(true);
            let boardsData = [];

            // Fetch boards from the primary endpoint.  Any error here is always
            // propagated — the outer catch will show the user a toast.
            boardsData = await kanbanBoardService.fetchBoards(apiFetch);

            // Admin fallback: only when the primary endpoint succeeded but returned
            // no boards.  Wrap in its own try-catch so a 403 (user has admin-page
            // permissions but not the Admin JWT role) does not crash the whole load.
            if (boardsData.length === 0 && IsAdmin) {
                try {
                    const adminBoards = await kanbanBoardService.fetchAllBoardsAdmin(apiFetch);
                    boardsData = adminBoards
                        .filter(board => board.isActive)
                        .map(board => ({
                            ...board,
                            actionItemCount: board.actionItemCount ?? 0,
                            permissionLevel: 'Admin',
                            hasSettingsAccess: true,
                            hasInternalNoteAccess: true
                        }));
                } catch (adminError) {
                    // The admin fallback failed (e.g. user has admin-page permissions
                    // but not the "Admin" JWT role).  Log it and continue with an empty
                    // board list rather than crashing the page.
                    Sentry.captureException(adminError);
                    console.warn('Admin all-boards fallback failed (likely insufficient JWT role):', adminError);
                }
            }

            setBoards(boardsData);

            // If a board was previously selected (e.g. from session), verify it still exists
            if (selectedBoardId && !boardsData.find(b => b.boardId === selectedBoardId)) {
                // Previously selected board no longer accessible — reset
                const defaultBoard = boardsData.find(b => b.isDefault) || boardsData[0];
                setSelectedBoardId(defaultBoard?.boardId || null);
            } else if (!selectedBoardId && boardsData.length > 0) {
                // No board selected yet — auto-select the default
                const defaultBoard = boardsData.find(b => b.isDefault) || boardsData[0];
                setSelectedBoardId(defaultBoard.boardId);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Failed to load boards:', error);
            toast.error('Failed to load boards');
        } finally {
            setLoadingBoards(false);
        }
        // Note: selectedBoardId is intentionally omitted from dependencies to prevent race condition
        // where setting the board ID would trigger loadBoards to re-run, potentially clearing the selection
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiFetch, IsAdmin]);

    /**
     * Delete a board
     */
    const handleDeleteBoard = useCallback(async (boardId) => {
        try {
            await kanbanBoardService.deleteBoard(apiFetch, boardId);
            toast.success('Board deleted successfully');
            await loadBoards();
            
            // If deleted board was selected, clear selection
            if (selectedBoardId === boardId) {
                setSelectedBoardId(null);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Failed to delete board:', error);
            toast.error('Failed to delete board');
        }
        // setSelectedBoardId is a stable state-setter reference but must be included
        // in the dependency array to satisfy react-hooks/exhaustive-deps.
    }, [apiFetch, loadBoards, selectedBoardId, setSelectedBoardId]);



    /**
 * Build the shared query-string parameters from the current filter state.
 * These are sent to every /by-column call so the server applies the same
 * filters across all columns.
 */
    const buildFilterParams = useCallback(() => {
        const params = new URLSearchParams();
        if (selectedBoardId) params.append('boardId', selectedBoardId);
        if (searchQuery.trim()) params.append('search', searchQuery.trim());
        if (sortField) params.append('sortField', sortField);
        if (sortDirection) params.append('sortDirection', sortDirection);
        if (filterPriority) params.append('priority', filterPriority);
        if (filterProperty) params.append('propertyId', filterProperty);
        if (filterAssignee) params.append('assignee', filterAssignee);
        if (filterCreator) params.append('creator', filterCreator);
        if (filterSource) params.append('source', filterSource);
        if (filterDatePreset) params.append('datePreset', filterDatePreset);
        if (filterDatePreset === 'custom') {
            if (filterDateFrom) params.append('dateFrom', filterDateFrom);
            if (filterDateTo) params.append('dateTo', filterDateTo);
        }
        return params;
    }, [selectedBoardId, searchQuery, sortField, sortDirection, filterPriority,
        filterProperty, filterAssignee, filterCreator, filterSource,
        filterDatePreset, filterDateFrom, filterDateTo]);

    /**
     * Fetch items for a single column/status. Returns the response data or null on error.
     * Can be called for initial load (page 1) or "Load More" (page N).
     */
    const fetchColumnItems = useCallback(async (status, page = 1) => {
        const params = buildFilterParams();
        params.append('status', status);
        params.append('page', page.toString());
        params.append('pageSize', COLUMN_PAGE_SIZE.toString());
        if (status === 'Closed') params.append('includeClosed', 'true');

        try {
            const response = await apiFetch(`/api/ActionItems/by-column?${params}`);
            const data = await response.json();
            if (data.success) return data;
        } catch (e) {
            Sentry.captureException(e);
            console.error(`Failed to fetch column "${status}":`, e);
        }
        return null;
    }, [apiFetch, buildFilterParams]);

    /**
     * Fetch page 1 for ALL visible columns in parallel.
     * Merges results into a single actionItems array and updates columnMeta.
     * This is the primary data-loading function — called on mount, board change,
     * and whenever filters/search/sort change (debounced).
     */
    const fetchAllColumns = useCallback(async () => {
        const requestId = actionItemsRequestRef.current + 1;
        actionItemsRequestRef.current = requestId;
        const boardIdAtStart = selectedBoardId;

        // Use custom board column statuses when available, otherwise fall back to hardcoded list.
        // This ensures custom statuses (e.g. "New", "In Review") are fetched too.
        const customStatuses = boardColumns.length > 0
            ? boardColumns.map(c => c.statusValue)
            : null;
        const allStatuses = customStatuses ?? STATUSES;
        const activeStatuses = allStatuses.filter(s => s !== 'Closed');
        const statusesToFetch = showClosedItems ? allStatuses : activeStatuses;

        // Mark all columns as loading
        setColumnMeta(prev => {
            const next = { ...prev };
            for (const s of statusesToFetch) {
                next[s] = { ...(next[s] || {}), loading: true };
            }
            return next;
        });

        // Fire all column requests in parallel
        const results = await Promise.all(
            statusesToFetch.map(async (status) => {
                const data = await fetchColumnItems(status, 1);
                return { status, data };
            })
        );

        // Stale guard — if the board changed or a newer request was issued, discard
        if (requestId !== actionItemsRequestRef.current || selectedBoardIdRef.current !== boardIdAtStart) {
            return;
        }

        // Merge all column items into one flat array and build metadata
        const allItems = [];
        const newMeta = {};
        for (const { status, data } of results) {
            if (data) {
                allItems.push(...data.items);
                newMeta[status] = {
                    page: data.page,
                    totalCount: data.totalCount,
                    hasMore: data.hasMore,
                    loading: false
                };
            } else {
                newMeta[status] = { page: 1, totalCount: 0, hasMore: false, loading: false };
            }
        }

        setActionItems(allItems);
        setColumnMeta(newMeta);
    }, [selectedBoardId, showClosedItems, fetchColumnItems, boardColumns]);

    /**
     * Load more items for a specific column (append to existing items).
     */
    const loadMoreColumnItems = useCallback(async (status) => {
        const currentPage = columnMeta[status]?.page || 1;
        const nextPage = currentPage + 1;

        setColumnMeta(prev => ({
            ...prev,
            [status]: { ...prev[status], loading: true }
        }));

        const data = await fetchColumnItems(status, nextPage);

        if (data) {
            // Append new items (avoid duplicates by ID)
            setActionItems(prev => {
                const existingIds = new Set(prev.map(i => i.id));
                const newItems = data.items.filter(i => !existingIds.has(i.id));
                return [...prev, ...newItems];
            });

            setColumnMeta(prev => ({
                ...prev,
                [status]: {
                    page: data.page,
                    totalCount: data.totalCount,
                    hasMore: data.hasMore,
                    loading: false
                }
            }));
        } else {
            setColumnMeta(prev => ({
                ...prev,
                [status]: { ...prev[status], loading: false }
            }));
        }
    }, [columnMeta, fetchColumnItems]);

    /**
     * React to showClosedItems toggle changes.
     * Toggle ON  → re-fetch all columns (which now includes Closed).
     * Toggle OFF → strip closed items from local state, clear Closed meta.
     */
    const showClosedItemsPrevRef = useRef(undefined);
    useEffect(() => {
        if (showClosedItemsPrevRef.current === undefined) {
            showClosedItemsPrevRef.current = showClosedItems;
            return;
        }
        if (showClosedItemsPrevRef.current === showClosedItems) return;
        showClosedItemsPrevRef.current = showClosedItems;

        if (showClosedItems) {
            fetchAllColumns();
        } else {
            setActionItems(prev => prev.filter(item => item.status !== 'Closed'));
            setColumnMeta(prev => {
                const next = { ...prev };
                delete next['Closed'];
                return next;
            });
        }
    }, [showClosedItems, fetchAllColumns]);

    /**
     * SignalR real-time updates for board changes
     */
    const boardSignalRCallbacks = useMemo(() => ({
        onBoardUpdated: (data) => {
            console.log('[Board SignalR] Board updated:', data);
            // Reload boards to get latest changes
            loadBoards();
        },
        onColumnsChanged: (data) => {
            console.log('[Board SignalR] Columns changed:', data);
            // Reload boards and columns to get updated state
            loadBoards();
            // Reload board columns inline
            if (selectedBoardId && selectedBoardId !== -1) {
                kanbanBoardService.fetchBoard(apiFetch, selectedBoardId).then(boardData => {
                    if (boardData?.columns && boardData.columns.length > 0) {
                        setBoardColumns(boardData.columns);
                    } else {
                        setBoardColumns([]);
                    }
                }).catch(() => {});
            }
        },
        onItemAddedToBoard: (data) => {
            console.log('[Board SignalR] Item added to board:', data);
            // Refresh action items if it affects current board
            if (data.BoardId === selectedBoardId) {
                fetchAllColumns();
            }
        },
        onItemRemovedFromBoard: (data) => {
            console.log('[Board SignalR] Item removed from board:', data);
            // Refresh action items if it affects current board
            if (data.BoardId === selectedBoardId) {
                fetchAllColumns();
            }
        },
        onItemUpdated: (data) => {
            console.log('[Board SignalR] ItemUpdated:', data);
            if (data.boardId === selectedBoardId) {
                if (data.changeType === 'AutomationRuleExecuted' && data.changes?.oldStatus && data.changes?.newStatus) {
                    const statusesToRefresh = [...new Set([data.changes.oldStatus, data.changes.newStatus])];
                    Promise.all(statusesToRefresh.map(status => fetchColumnItems(status, 1))).then(results => {
                        results.forEach((result, idx) => {
                            if (!result) return;
                            const status = statusesToRefresh[idx];
                            setActionItems(prev => {
                                const otherItems = prev.filter(i => i.status !== status);
                                return [...otherItems, ...result.items];
                            });
                            setColumnMeta(prev => ({
                                ...prev,
                                [status]: { page: result.page, totalCount: result.totalCount, hasMore: result.hasMore, loading: false }
                            }));
                        });
                    });
                } else {
                    fetchAllColumns();
                }
            }
        },
        onAccessGranted: (data) => {
            console.log('[Board SignalR] Access granted:', data);
            toast.info('Board access updated');
            loadBoards();
        },
        onAccessRevoked: (data) => {
            console.log('[Board SignalR] Access revoked:', data);
            toast.warning('Board access revoked');
            loadBoards();
        },
        onBoardListChanged: () => {
            console.log('[Board SignalR] Board list changed — reloading boards');
            loadBoards();
        }
    }), [selectedBoardId, loadBoards, fetchAllColumns, fetchColumnItems, setActionItems, setColumnMeta]);

    // Connect to board SignalR hub when a board is selected
    const { broadcastColumnsChange } = useActionItemBoardSignalR(
        selectedBoardId,
        boardSignalRCallbacks,
        !!selectedBoardId // Only connect if a board is selected
    );

    /**
     * Helper function to get the display text for an action item.
     * Provides consistent fallback logic across search, display, and export.
     * Enhancement 1: Prioritize title field as the primary display text for action items.
     * This ensures titles are always shown in list, kanban, and gantt views.
     * @param {Object} item - The action item object
     * @returns {string} The display text for the item
     */
    const getItemDisplayText = (item) => {
        // Priority: title > description > nonCompliance > empty string
        return item.title || item.description || item.nonCompliance || '';
    };

    /**
     * Helper function to determine if an action item needs the current user's attention.
     * Returns true if the item has the attention flag set AND the current user is the assignee.
     * @param {Object} item - The action item object
     * @returns {boolean} True if item needs current user's attention
     */
    const needsMyAttention = (item) => {
        return item.needsAttention && item.assignedToUserID === userID;
    };

    /**
     * Helper function to get the icon for an action item based on its template.
     * Looks up the template by templateKey and returns the icon, falling back to a default.
     * @param {Object} item - The action item object
     * @returns {string} FontAwesome icon class or emoji
     */
    const getItemIcon = (item) => {
        // Find matching template by templateKey or actionItemsType
        const template = templates.find(t => 
            t.templateKey === item.templateKey || 
            t.templateKey === item.actionItemsType
        );
        
        // Return template icon if found, otherwise return a default icon
        if (template && template.icon) {
            return template.icon;
        }
        
        // Default icons based on action item type
        if (item.actionItemsType === 'Non-Compliance' || item.templateKey === 'NonCompliance') {
            return 'fa-exclamation-triangle';
        }
        if (item.actionItemsType === 'Manual' || item.templateKey === 'Manual') {
            return 'fa-tasks';
        }
        
        // Generic default
        return 'fa-clipboard-list';
    };

    /**
     * Fetches action item templates from the API.
     * Templates define the structure, fields, and validation for different action item types.
     */
    const fetchTemplates = useCallback(async () => {
        try {
            setLoadingTemplates(true);
            const response = await apiFetch('/api/Templates');
            const data = await response.json();
            if (data.success && data.data) {
                setTemplates(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error("Error fetching templates:", e);
            toast.error("Failed to load action item templates");
        } finally {
            setLoadingTemplates(false);
        }
    }, [apiFetch]);

    /**
     * Fetches the Ad-Hoc template assignment from ModuleTemplateAssignments.
     * This determines which template to use when creating action items from this page.
     * The template schema is then used to render the form fields.
     */
    const fetchAdHocTemplate = useCallback(async () => {
        try {
            setLoadingAdHocTemplate(true);
            
            // First get the template assignment for Ad-Hoc context
            const assignmentResponse = await apiFetch('/api/ModuleTemplateAssignments/General/AdHoc');
            const assignmentData = await assignmentResponse.json();
            
            let templateKey = null;
            if (assignmentResponse.ok && assignmentData.success && assignmentData.data?.assignedTemplateKey) {
                templateKey = assignmentData.data.assignedTemplateKey;
            }
            
            // If we have a template key, fetch the full template schema
            if (templateKey) {
                const templateResponse = await apiFetch(`/api/Templates/${templateKey}`);
                const templateData = await templateResponse.json();
                if (templateResponse.ok && templateData.success && templateData.data) {
                    setAdHocTemplate(templateData.data);
                }
            } else {
                // No Ad-Hoc template assigned - use default Manual template behavior
                setAdHocTemplate({
                    templateKey: 'Manual',
                    displayName: 'Ad-Hoc Action Item',
                    description: 'Create a new ad-hoc action item',
                    defaultPriority: 'Low',
                    schema: JSON.stringify({
                        fields: [
                            { name: 'title', label: 'Title', type: 'text', required: true },
                            { name: 'description', label: 'Description', type: 'textarea', required: false }
                        ]
                    })
                });
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error("Error fetching Ad-Hoc template:", e);
            // Fallback to default Manual behavior
            setAdHocTemplate({
                templateKey: 'Manual',
                displayName: 'Ad-Hoc Action Item',
                description: 'Create a new ad-hoc action item',
                defaultPriority: 'Low',
                schema: JSON.stringify({
                    fields: [
                        { name: 'title', label: 'Title', type: 'text', required: true },
                        { name: 'description', label: 'Description', type: 'textarea', required: false }
                    ]
                })
            });
        } finally {
            setLoadingAdHocTemplate(false);
        }
    }, [apiFetch]);

    /**
     * Fetches the board creation types the current user is allowed to create.
     * Each type has a templateKey that determines the form schema.
     */
    const fetchBoardCreationTypes = useCallback(async () => {
        try {
            setLoadingCreationTypes(true);
            const res = await apiFetch('/api/BoardCreationRules/my-types');
            const data = await res.json();
            if (data.success && data.data) {
                setBoardCreationTypes(data.data);
                // Auto-select the first available type
                if (data.data.length > 0) {
                    setSelectedCreationType(data.data[0]);
                }
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching board creation types:', e);
        } finally {
            setLoadingCreationTypes(false);
        }
    }, [apiFetch]);

    // Fetches all Action Items for the current user (includes all types: Non-Compliance, SIPA, Ad-Hoc, Project-related)
    const handleAssigneeChange = async (itemId, assignedToUserId) => {
        try {
            await apiFetch('/api/ActionItems/assign-user', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    actionItemId: itemId,
                    assignedToUserId,
                    changedByUserId: user.userID
                })
            });
            fetchAllColumns();
            toast.success('Assignment updated successfully');
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const handlePriorityChange = async (itemId, newPriority = "Low") => {
        try {
            await apiFetch(`/api/ActionItems/action-items/${itemId}/priority`, {
                method:'PUT',
                headers:{'Content-Type':'application/json'},
                body: JSON.stringify({
                    priority:newPriority,
                    userId:user.userID
                })
            });
            fetchAllColumns();
            toast.success('Priority updated successfully');
        } catch(e){
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const handleStatusChange = async (itemId, newStatus) => {
        try {
            await apiFetch(`/api/ActionItems/action-items/${itemId}/status`, {
                method:'PUT',
                headers:{'Content-Type':'application/json'},
                body: JSON.stringify({
                    status:newStatus,
                    userID:user.userID
                })
            });
            fetchAllColumns();
            toast.success('Status updated successfully');
        } catch(e){
            Sentry.captureException(e);
            console.error(e);
        }
    };

    /**
     * Fetches the property data for the dropdown selection.
     * Wrapped in useCallback to provide a stable function reference for useEffect dependencies
     * and satisfy exhaustive-deps linting rules.
     */
    const fetchProperties = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Property/all');
            const data = await response.json();
            if (data.success) {
                setPropertiesMDA(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error("Error fetching properties:", e);
        }
    }, [apiFetch]);

    /**
     * Fetches all users for assignee dropdown selection.
     * Wrapped in useCallback to provide a stable function reference for useEffect dependencies
     * and satisfy exhaustive-deps linting rules.
     */
    const fetchAllUsers = useCallback(async () => {
        try {
            const response = await apiFetch('/api/User/all');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setAllUsers(data.data);
            } else {
                setAllUsers([]);
            }
        } catch (e) {
            Sentry.captureException(e);
            setAllUsers([]);
            console.error("Error fetching all users:", e);
        }
    }, [apiFetch]);

    /**
     * Fetches all projects for linking project-based action items.
     * Wrapped in useCallback to provide a stable function reference for useEffect dependencies.
     */
    const fetchProjects = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Projects?includeInactive=true');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setProjects(data.data);
            } else {
                setProjects([]);
            }
        } catch (e) {
            Sentry.captureException(e);
            setProjects([]);
            console.debug("Error fetching projects:", e);
        }
    }, [apiFetch]);

    /**
     * Fetches active functions for function-based assignment and swimlane views.
     * Wrapped in useCallback to provide a stable function reference for useEffect dependencies.
     */
    const fetchFunctions = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Functions?includeInactive=false');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setFunctions(data.data);
            } else {
                setFunctions([]);
            }
        } catch (e) {
            Sentry.captureException(e);
            setFunctions([]);
            console.debug("Error fetching functions:", e);
        }
    }, [apiFetch]);

    /**
     * Fetches active departments for department-based assignment.
     * Wrapped in useCallback to provide a stable function reference for useEffect dependencies.
     */
    const fetchDepartments = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Department?includeInactive=false');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setDepartments(data.data);
            } else {
                setDepartments([]);
            }
        } catch (e) {
            Sentry.captureException(e);
            setDepartments([]);
            console.debug("Error fetching departments:", e);
        }
    }, [apiFetch]);

    /**
     * Fetches IT ticket categories from the ITSM endpoint.
     */
    const fetchItCategories = useCallback(async () => {
        try {
            const response = await apiFetch('/api/ITServiceDesk/categories');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setItCategories(data.data.filter(c => c.isActive));
            }
        } catch (e) {
            Sentry.captureException(e);
            console.debug('Error fetching IT categories:', e);
        }
    }, [apiFetch]);

    /**
     * Helper function to get project name from project ID
     */
    const getProjectName = (projectId) => {
        if (!projectId) return null;
        const project = projects.find(p => p.projectId === projectId);
        return project?.name || null;
    };

    const prevFetchRef = useRef(fetchAllColumns);

    /**
     * Effect: fetch all columns when board changes (immediate) or when
     * filters/search/sort change (debounced 300ms).
     */
    useEffect(() => {
        const fetchChanged = prevFetchRef.current !== fetchAllColumns;
        prevFetchRef.current = fetchAllColumns;
        selectedBoardIdRef.current = selectedBoardId;
        actionItemsRequestRef.current += 1;

        // Clear existing debounce
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        const doFetch = async () => {
            // When the board or filters changed, or we have no cached items,
            // clear the screen and do a full load (shows loading state).
            if (fetchChanged || actionItems.length === 0) {
                setActionItems([]);
                setColumnMeta({});
                setSelectedItems(new Set());
            }
            // Always fetch from the server. When returning from a detail page
            // (fetchChanged === false, cached items exist) this acts as a
            // background refresh — the user sees the cached board instantly
            // while fresh data replaces it once the API responds. This fixes
            // the bug where assignment/status/priority changes made on the
            // detail page were not reflected on the board until the 60-second
            // polling cycle ran.
            await fetchAllColumns();
        };

        // On board change → fetch immediately. On filter/search change → debounce.
        searchDebounceRef.current = setTimeout(doFetch, SEARCH_DEBOUNCE_MS);

        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, [selectedBoardId, fetchAllColumns]);

    /**
     * Effect to fetch initial data: templates, properties, users, projects, functions, departments, boards, and Ad-Hoc template.
     * Dependencies include all fetch functions to satisfy exhaustive-deps linting.
     *
     * All API calls are independent of each other so they are dispatched in parallel
     * with Promise.all for the fastest possible initial page load. The async wrapper
     * also satisfies react-hooks/set-state-in-effect — setState calls originating from
     * these fetch functions happen asynchronously, not synchronously in the effect body.
     */
    useEffect(() => {
        const loadInitialData = async () => {
            await Promise.all([
                fetchTemplates(),
                fetchAdHocTemplate(),
                fetchBoardCreationTypes(),
                fetchProperties(),
                fetchAllUsers(),
                fetchProjects(),
                fetchFunctions(),
                fetchDepartments(),
                fetchItCategories(),
                loadBoards()
            ]);
        };
        loadInitialData();
    }, [fetchTemplates, fetchAdHocTemplate, fetchBoardCreationTypes, fetchProperties, fetchAllUsers, fetchProjects, fetchFunctions, fetchDepartments, fetchItCategories, loadBoards]);

    /**
     * Load user preferences once on mount.
     * Applies default view mode and swimlane group-by ONLY if the user hasn't
     * already set a session value (session state takes precedence so manual
     * changes during a browsing session aren't overwritten).
     */
    useEffect(() => {
        const loadUserPreferences = async () => {
            try {
                const res = await apiFetch(`/api/UserPreferences/${userID}`);
                const data = await res.json();
                if (data.success && data.data) {
                    const prefs = data.data;
                    // Only apply defaults if not already session-overridden
                    if (prefs.defaultActionItemView && !sessionStorage.getItem('ai_viewMode')) {
                        setViewMode(prefs.defaultActionItemView);
                    }
                    if (prefs.defaultSwimGroupBy && !sessionStorage.getItem('ai_swimGroupBy')) {
                        setSwimGroupBy(prefs.defaultSwimGroupBy);
                    }
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Preferences not available — use defaults
            }
        };
        loadUserPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Loads columns for the selected board.
     * Uses the board detail endpoint to get the configured columns.
     */
    const loadBoardColumns = useCallback(async () => {
        const requestId = boardColumnsRequestRef.current + 1;
        boardColumnsRequestRef.current = requestId;
        const boardIdAtRequestStart = selectedBoardId;

        if (!selectedBoardId || selectedBoardId === -1) {
            setBoardColumns([]);
            return;
        }
        try {
            const boardData = await kanbanBoardService.fetchBoard(apiFetch, boardIdAtRequestStart);

            if (requestId !== boardColumnsRequestRef.current || selectedBoardIdRef.current !== boardIdAtRequestStart) {
                return;
            }

            if (boardData?.columns && boardData.columns.length > 0) {
                setBoardColumns(boardData.columns);
            } else {
                setBoardColumns([]);
            }
        } catch (e) {
            if (requestId !== boardColumnsRequestRef.current || selectedBoardIdRef.current !== boardIdAtRequestStart) {
                return;
            }

            Sentry.captureException(e);
            console.error('Failed to load board columns:', e);
            setBoardColumns([]);
        }
    }, [selectedBoardId, apiFetch]);

    useEffect(() => {
        // Update the ref BEFORE starting the load so that the staleness guard inside
        // loadBoardColumns sees the latest board ID.  This is important because the
        // action-items effect (declared later) also writes selectedBoardIdRef, but there
        // is no guaranteed execution order between the two effects when both are triggered
        // by the same selectedBoardId change — setting the ref here ensures the check is
        // always accurate for this effect's own load.
        selectedBoardIdRef.current = selectedBoardId;
        boardColumnsRequestRef.current += 1;
        const load = async () => { await loadBoardColumns(); };
        load();
    }, [selectedBoardId, loadBoardColumns]);

    /**
 * Polling effect — silently refreshes all columns every POLLING_INTERVAL ms.
 * Pauses when the tab is hidden.
 */
    useEffect(() => {
        let pollInterval;

        const pollAllColumns = async () => {
            try {
                const customStatuses = boardColumns.length > 0
                    ? boardColumns.map(c => c.statusValue)
                    : null;
                const allStatuses = customStatuses ?? STATUSES;
                const activeStatuses = allStatuses.filter(s => s !== 'Closed');
                const statusesToFetch = showClosedItems ? allStatuses : activeStatuses;  // ← was a single hardcoded line before
                const results = await Promise.all(
                    statusesToFetch.map(async (status) => {
                        const currentPage = columnMeta[status]?.page || 1;
                        const params = buildFilterParams();
                        params.append('status', status);
                        params.append('page', '1');
                        params.append('pageSize', (currentPage * COLUMN_PAGE_SIZE).toString());
                        if (status === 'Closed') params.append('includeClosed', 'true');

                        const response = await apiFetch(`/api/ActionItems/by-column?${params}`);
                        const data = await response.json();
                        return { status, data: data.success ? data : null };
                    })
                );

                const allItems = [];
                const newMeta = {};
                for (const { status, data } of results) {
                    if (data) {
                        allItems.push(...data.items);
                        newMeta[status] = {
                            page: columnMeta[status]?.page || 1,
                            totalCount: data.totalCount,
                            hasMore: data.hasMore,
                            loading: false
                        };
                    }
                }
                setActionItems(allItems);
                setColumnMeta(prev => ({ ...prev, ...newMeta }));
            } catch (e) {
                Sentry.captureException(e);
                console.debug("Polling error:", e);
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
            } else {
                if (!pollInterval) {
                    pollAllColumns();
                    pollInterval = setInterval(pollAllColumns, POLLING_INTERVAL);
                }
            }
        };

        pollInterval = setInterval(pollAllColumns, POLLING_INTERVAL);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (pollInterval) clearInterval(pollInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [apiFetch, showClosedItems, columnMeta, buildFilterParams, boardColumns]);  // ← boardColumns added

    /**
     * Handle when dragging starts
     */
    const handleDragStart = ({ active }) => {
        setDraggedItemId(active.id);
        // Snapshot the dragged item so the DragOverlay can keep rendering it
        // even after a cross-board hover swaps `actionItems` out from under us.
        const snapshot = actionItems.find(i => i.id.toString() === active.id.toString());
        setDraggedItemSnapshot(snapshot || null);
        // Clear any pending sidebar-open timer from a previous drag
        if (boardsTabHoverRef.current) {
            clearTimeout(boardsTabHoverRef.current);
            boardsTabHoverRef.current = null;
        }
        // If the sidebar is already open when drag starts, treat it as auto-opened
        // so it will collapse again after the drag finishes (unless user drops on a
        // board, which keeps the user on that board's sidebar context).
        if (showBoardSidebar) {
            autoOpenedSidebarRef.current = true;
        }
    };

    /**
     * Handle drag-over events to detect when dragging over the BOARDS tab.
     * Uses a 400ms debounce so the sidebar only opens when the user deliberately
     * holds the dragged item over the BOARDS tab button — not when collision
     * detection briefly resolves to it while dragging from the first column.
     */
    const handleDragOver = ({ over }) => {
        if (over?.data?.current?.type === 'boards-tab' && !showBoardSidebar) {
            // Start a timer if not already running
            if (!boardsTabHoverRef.current) {
                boardsTabHoverRef.current = setTimeout(() => {
                    setShowBoardSidebar(true);
                    autoOpenedSidebarRef.current = true;
                    boardsTabHoverRef.current = null;
                }, 800);
            }
        } else {
            // Pointer moved away from the boards-tab — cancel the pending timer
            if (boardsTabHoverRef.current) {
                clearTimeout(boardsTabHoverRef.current);
                boardsTabHoverRef.current = null;
            }
        }
    };

    /**
     * Handle when hovering over a board in the sidebar during drag
     */
    const handleBoardHover = (boardId) => {
        // Clear existing timeout
        if (hoverTimeout) {
            clearTimeout(hoverTimeout);
        }

        if (boardId === null) {
            // Mouse left the board - clear preview
            setPreviewBoardId(null);
            setHoverTimeout(null);
        } else if (boardId !== selectedBoardId) {
            // Mouse entered a different board - set timeout to preview
            const timeout = setTimeout(() => {
                setPreviewBoardId(boardId);
                setSelectedBoardId(boardId);
            }, 800); // 800ms delay before switching board
            setHoverTimeout(timeout);
        }
    };

    // Functionality for after a user has clicked and dragged a action item to a different col
    const handleDragEnd = async ({ active, over }) => {
        // Clear drag state
        setDraggedItemId(null);
        setDraggedItemSnapshot(null);
        setPreviewBoardId(null);
        if (hoverTimeout) {
            clearTimeout(hoverTimeout);
            setHoverTimeout(null);
        }
        // Clear boards-tab hover timer
        if (boardsTabHoverRef.current) {
            clearTimeout(boardsTabHoverRef.current);
            boardsTabHoverRef.current = null;
        }

        // Auto-close sidebar if it was opened automatically during this drag
        // (will be kept open only if dropped on the BOARDS tab intentionally)
        const wasAutoOpened = autoOpenedSidebarRef.current;
        autoOpenedSidebarRef.current = false;

        if (!over || !active) {
            if (wasAutoOpened) setShowBoardSidebar(false);
            return;
        }

        const itemId = active.id;
        // The live actionItems list may have been swapped to a different
        // board mid-drag by the sidebar hover-switch, in which case the
        // dragged item won't be present. Fall back to the snapshot captured
        // at drag-start so we can still identify the item on drop.
        const item = actionItems.find(i => i.id.toString() === itemId)
            || draggedItemSnapshot
            || null;
        if (!item) {
            if (wasAutoOpened) setShowBoardSidebar(false);
            return;
        }

        // Check if dropped on the BOARDS tab (open sidebar)
        if (over.data?.current?.type === 'boards-tab') {
            setShowBoardSidebar(true);
            return;
        }

        // Check if dropped on a board (cross-board transfer)
        if (over.data?.current?.type === 'board') {
            const targetBoardId = over.data.current.boardId;

            // Don't transfer if it's the same board
            if (targetBoardId === selectedBoardId) {
                toast.info('Action item is already on this board');
                return;
            }

            // Validate itemId is a valid number
            const actionItemId = parseInt(itemId, 10);
            if (isNaN(actionItemId)) {
                console.error('Invalid action item ID:', itemId);
                toast.error('Invalid action item ID');
                return;
            }

            // Find the target board name for better user feedback
            const targetBoard = boards.find(b => b.boardId === targetBoardId);
            const targetBoardName = targetBoard?.name || 'target board';

            try {
                // Move item to target board by updating its BoardId
                const response = await apiFetch(`/api/ActionItems/${actionItemId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        boardId: targetBoardId,
                        boardIdProvided: true,
                        userId: userID
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || 'Failed to move action item');
                }

                toast.success(`Action item moved to "${targetBoardName}" successfully`);

                // Close sidebar after successful transfer
                setShowBoardSidebar(false);

                // Switch to the target board and refresh items
                setSelectedBoardId(targetBoardId);
                fetchAllColumns();
            } catch (error) {
                Sentry.captureException(error);
                console.error('Failed to transfer action item to board:', error);
                toast.error(error.message || 'Failed to move action item to board');
            }
            return;
        }

        // Normal status change (drag between columns on same board)
        // Close auto-opened sidebar since the drop wasn't on a board
        if (wasAutoOpened) setShowBoardSidebar(false);

        const newStatus = over.data?.current?.status || over.id;

        // Cross-board column drop: the sidebar hover-switch rendered a
        // different board's columns while the drag was still in flight.
        // Persist both the board move and the new status in a single PUT.
        //
        // The condition intentionally allows `originBoardId = null` so that
        // action items with no board assignment are correctly assigned to the
        // target board when dropped onto one of its columns. Previously,
        // `originBoardId != null` prevented this case from firing, leaving
        // the boardId unchanged even though the user dropped onto another board.
        // Dual-casing fallback: items fetched via the modern API return camelCase `boardId`.
        // Items originating from SignalR notifications or older code paths may carry PascalCase
        // `BoardId` inherited from the C# DTO serialization. The `?? null` makes the intent
        // explicit and avoids an implicit `undefined` slipping through.
        const originBoardId = item.boardId ?? item.BoardId ?? null;
        const isCrossBoardColumnDrop = selectedBoardId != null
            && originBoardId !== selectedBoardId;

        if (isCrossBoardColumnDrop) {
            const targetBoardId = selectedBoardId;
            const targetBoardName = boards.find(b => b.boardId === targetBoardId)?.name || 'target board';
            const actionItemId = parseInt(itemId, 10);
            if (isNaN(actionItemId)) {
                toast.error('Invalid action item ID');
                return;
            }
            try {
                const response = await apiFetch(`/api/ActionItems/${actionItemId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        boardId: targetBoardId,
                        boardIdProvided: true,
                        status: newStatus,
                        userId: userID
                    })
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || 'Failed to move action item');
                }
                toast.success(`Action item moved to "${targetBoardName}" (${newStatus})`);
                setShowBoardSidebar(false);
                fetchAllColumns();
            } catch (error) {
                Sentry.captureException(error);
                console.error('Failed to transfer action item to board column:', error);
                toast.error(error.message || 'Failed to move action item');
            }
            return;
        }

        if (item.status !== newStatus) {
            const oldStatus = item.status;

            // Optimistic update: immediately move the item in the list
            setActionItems(prevItems =>
                prevItems.map(i =>
                    i.id.toString() === itemId ? { ...i, status: newStatus } : i
                )
            );

            // Optimistic update: adjust column header counts immediately
            setColumnMeta(prev => ({
                ...prev,
                [oldStatus]: {
                    ...prev[oldStatus],
                    totalCount: Math.max(0, (prev[oldStatus]?.totalCount ?? 0) - 1)
                },
                [newStatus]: {
                    ...prev[newStatus],
                    totalCount: (prev[newStatus]?.totalCount ?? 0) + 1
                }
            }));

            try {
                const payload = {
                    status: newStatus,
                    userID
                }
                const response = await apiFetch(`/api/ActionItems/action-items/${itemId}/status`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    // Revert both optimistic updates on error
                    setActionItems(prevItems =>
                        prevItems.map(i =>
                            i.id.toString() === itemId ? { ...i, status: oldStatus } : i
                        )
                    );
                    setColumnMeta(prev => ({
                        ...prev,
                        [oldStatus]: {
                            ...prev[oldStatus],
                            totalCount: (prev[oldStatus]?.totalCount ?? 0) + 1
                        },
                        [newStatus]: {
                            ...prev[newStatus],
                            totalCount: Math.max(0, (prev[newStatus]?.totalCount ?? 0) - 1)
                        }
                    }));
                    toast.error("Failed to update status");
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error("Failed to update status:", e);
                // Revert both optimistic updates on error
                setActionItems(prevItems =>
                    prevItems.map(i =>
                        i.id.toString() === itemId ? { ...i, status: oldStatus } : i
                    )
                );
                setColumnMeta(prev => ({
                    ...prev,
                    [oldStatus]: {
                        ...prev[oldStatus],
                        totalCount: (prev[oldStatus]?.totalCount ?? 0) + 1
                    },
                    [newStatus]: {
                        ...prev[newStatus],
                        totalCount: Math.max(0, (prev[newStatus]?.totalCount ?? 0) - 1)
                    }
                }));
                toast.error("Failed to update status");
            }
        }
    };

    // Fetch properties for the modal select dropdown
    useEffect(() => {
        const fetchPropertiesForModal = async () => {
            try {
                const response = await apiFetch('/api/Property/all');
                const data = await response.json();
                if (data.success && data.data) {
                    const activeProps = data.data.filter(p => p.active || p.Active);
                    setProperties(activeProps);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Handle error silently
            }
        };
        fetchPropertiesForModal();
    }, [apiFetch]);

    /**
     * Exports filtered action items to an Excel file.
     * Enhancement 1: Title is now the primary field in the export.
     */
    const handleExportToExcel = () => {
        const MAX_CELL_LENGTH = 32767;
        const truncate = (str) => str && str.length > MAX_CELL_LENGTH ? str.substring(0, MAX_CELL_LENGTH - 3) + '...' : str;

        const exportData = filteredItems.map(item => {
            const property = propertiesMDA?.find(p => p.propertyID === item.propertyID);
            const assignedUser = allUsers.find(u => u.id === item.assignedToUserID);
            const assignedName = getUserDisplayName(assignedUser) || 'Unassigned';
            return {
                'ID': item.id,
                'Title': truncate(item.title || ''),
                'Description': truncate(item.description || getItemDisplayText(item)),
                'Status': item.status,
                'Priority': item.priority,
                'Property': property?.propertyName || 'Unknown',
                'Tenant': item.tenant || '',
                'Assigned To': assignedName,
                'Created Date': item.createdDate ? formatCardDate(item.createdDate) : '',
                'Due Date': item.dueDate ? formatCardDate(item.dueDate) : '',
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Action Items');
        
        // Generate filename with current date
        const dateStr = new Date().toISOString().split('T')[0];
        XLSX.writeFile(workbook, `ActionItems_${dateStr}.xlsx`);
        toast.success('Exported to Excel successfully!');
    };

    /**
     * Clears all advanced filters.
     */
    const handleClearFilters = () => {
        setFilterStatus("");
        setFilterPriority("");
        setFilterProperty("");
        setFilterAssignee(""); // Reset to truly show all items
        setFilterCreator(""); // Reset creator filter
        setFilterSource(""); // Reset source filter
        setSearchQuery("");
        setShowClosedItems(false);
        setFilterDatePreset("");
        setFilterDateFrom("");
        setFilterDateTo("");
    };

    /**
     * Toggle selection of an action item in list view.
     * @param {number} itemId - The ID of the item to toggle
     */
    const handleToggleSelection = (itemId) => {
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    /**
     * Select all visible items in list view.
     */
    const handleSelectAll = () => {
        if (selectedItems.size === filteredItems.length) {
            // Deselect all
            setSelectedItems(new Set());
        } else {
            // Select all filtered items
            setSelectedItems(new Set(filteredItems.map(item => item.id)));
        }
    };

    /**
     * Clear all selections.
     */
    const handleClearSelections = () => {
        setSelectedItems(new Set());
    };

    /**
     * Open merge modal when multiple items are selected.
     */
    const handleOpenMergeModal = () => {
        if (selectedItems.size < 2) {
            toast.error('Select at least 2 items to merge');
            return;
        }
        // Default target is the first selected item (lowest ID)
        const sortedIds = Array.from(selectedItems).sort((a, b) => a - b);
        setMergeTargetId(sortedIds[0]);
        setShowMergeModal(true);
    };

    /**
     * Merge selected action items into a single target item via the backend merge endpoint.
     * The backend atomically reassigns all attachments from each source item to the target,
     * appends audit comments to both items, then closes the source items.
     * This ensures no uploaded files are ever lost during a merge.
     */
    const handleMergeItems = async () => {
        if (!mergeTargetId || selectedItems.size < 2) return;

        setIsMerging(true);
        try {
            const ItemsToMerge = Array.from(selectedItems).filter(id => id !== mergeTargetId);

            // Call the atomic backend merge endpoint for each source item
            for (const ItemId of ItemsToMerge) {
                const Response = await apiFetch(`/api/ActionItems/${ItemId}/merge`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        targetActionItemId: mergeTargetId,
                        userId: userID
                    })
                });
                const Data = await Response.json();
                if (!Data.success) {
                    toast.error(`Failed to merge item #${ItemId}: ${Data.message || 'Unknown error'}`);
                    return;
                }
            }

            toast.success(`Successfully merged ${ItemsToMerge.length} item(s) into Action Item #${mergeTargetId}`);
            setShowMergeModal(false);
            setSelectedItems(new Set());
            setMergeTargetId(null);
            fetchAllColumns();

            // Navigate to the target action item to review the merged result
            navigate(`/action-items/${mergeTargetId}`);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error merging items:', error);
            toast.error('An error occurred while merging action items.');
        } finally {
            setIsMerging(false);
        }
    };

    /**
     * Permanently deletes a single action item and all its related data.
     * Only visible to administrators. Shows a confirm dialog before proceeding.
     * @param {number} ItemId - The ID of the action item to delete
     */
    const handleDeleteItem = async (ItemId) => {
        if (!window.confirm(`Permanently delete Action Item #${ItemId}? This cannot be undone.`)) return;

        try {
            const Response = await apiFetch(`/api/ActionItems/${ItemId}`, { method: 'DELETE' });
            const Data = await Response.json();
            if (Data.success) {
                toast.success(`Action item #${ItemId} permanently deleted.`);
                fetchAllColumns();
            } else {
                toast.error(Data.message || 'Failed to delete action item.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while deleting the action item.');
        }
    };

    /**
     * Helper function to check if user is an observer on an action item.
     * @param {Object} item - The action item object with collaborators array
     * @param {number} userId - The user ID to check
     * @returns {boolean} True if user is an observer on the item
     */
    const isUserObserver = (item, userId) => {
        if (!item.collaborators || !Array.isArray(item.collaborators)) return false;
        return item.collaborators.some(c => 
            c.userId === userId && c.collaboratorRole === 'Observer'
        );
    };

    /**
     * Helper function to check if user is any type of collaborator on an action item.
     * @param {Object} item - The action item object with collaborators array
     * @param {number} userId - The user ID to check
     * @returns {boolean} True if user is a collaborator on the item
     */
    const isUserCollaborator = (item, userId) => {
        if (!item.collaborators || !Array.isArray(item.collaborators)) return false;
        return item.collaborators.some(c => c.userId === userId);
    };

    /**
     * Helper function to check if the current user is the creator/requester of an action item.
     * 
     * NOTE: This function checks both camelCase (requesterUserID) and PascalCase (RequesterUserID)
     * property names to handle different API response formats:
     * - The backend API returns camelCase (requesterUserID) in JSON responses due to ASP.NET Core's
     *   default JSON serialization settings.
     * - Some legacy data or cached responses may still use PascalCase (RequesterUserID).
     * 
     * @param {Object} item - The action item object
     * @param {number} userId - The user ID to check
     * @returns {boolean} True if user is the creator of the item
     */
    const isUserCreator = (item, userId) => {
        return item.requesterUserID === userId || item.RequesterUserID === userId ||
               item.userID === userId || item.UserID === userId;
    };

    /**
     * Helper function to check if an action item is assigned to the user's department.
     * Returns true if the item has AssignedToDepartmentID and it matches the user's department.
     * @param {Object} item - The action item object
     * @returns {boolean} True if item is assigned to user's department
     */
    const isAssignedToMyDepartment = (item) => {
        // The user object from login has camelCase properties (departmentID)
        if (!user.departmentID) return false;
        
        // Check if item has a department assignment that matches user's department
        return item.assignedToDepartmentID === user.departmentID;
    };

    /**
     * Converts a raw source/template key into a human-readable label for the
     * Source/Type filter dropdown.
     *
     * The previous single-regex approach inserted a space before EVERY uppercase
     * letter, which broke common acronyms: "CMDailyChecklistForm" became
     * "C M Daily Checklist Form" and "ITNetworkMonitoring" became
     * "I T Network Monitoring".
     *
     * The updated three-step approach correctly handles acronyms:
     *   Step 1 – replace dashes/underscores with spaces.
     *   Step 2 – insert a space between a run of uppercase letters and the last
     *             uppercase-lowercase pair that follows (e.g. "CMDa" → "CM Da").
     *   Step 3 – insert a space between a lowercase/digit and the next uppercase
     *             letter (e.g. "rM" → "r M").
     *
     * Examples:
     *   "CMDailyChecklistForm"  → "CM Daily Checklist Form"
     *   "ITNetworkMonitoring"   → "IT Network Monitoring"
     *   "RouterMonitoring"      → "Router Monitoring"
     *   "NonCompliance"         → "Non Compliance"
     *   "ProjectTask"           → "Project Task"
     *
     * @param {string} source - Raw source string stored in the database
     * @returns {string} Human-readable label
     */
    const formatSourceLabel = (source) => {
        if (!source) return source;
        return source
            // Replace dashes and underscores with spaces
            .replace(/[-_]/g, ' ')
            // Insert space between a consecutive run of uppercase letters and the
            // final uppercase letter that starts a new word (handles acronyms like CM, IT)
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
            // Insert space between a lowercase letter (or digit) and the next uppercase
            // letter (handles standard PascalCase / camelCase word boundaries)
            .replace(/([a-z\d])([A-Z])/g, '$1 $2')
            .trim();
    };

    /**
     * Derives the available Source/Type filter options dynamically from the current
     * actionItems state so that the dropdown always reflects what is actually
     * present in the data (including RouterMonitoring alerts, which are not in the
     * templates list).
     *
     * Precedence for labels:
     * 1. Project-based items  →  "Project Tasks"
     * 2. Items whose templateKey matches a loaded template  →  template.displayName
     * 3. Items with a source value not already covered  →  formatted source label
     *
     * The result is sorted alphabetically and is recalculated whenever actionItems
     * or templates change (this also covers the "Show Closed Items" toggle because
     * actionItems contains ALL loaded items — the closed-items filter is applied
     * downstream in filteredItems).
     */
    const availableSourceOptions = useMemo(() => {
        const options = [];
        const seenValues = new Set();

        // Build a quick lookup from templateKey → displayName
        const templateLookup = {};
        for (const template of templates) {
            if (template.templateKey) {
                templateLookup[template.templateKey] = template.displayName;
            }
        }

        // Walk every loaded action item and extract unique source options
        for (const item of actionItems) {
            // 1. Project-based items — detected by the presence of projectId
            if (item.projectId && !seenValues.has('ProjectTask')) {
                options.push({ value: 'ProjectTask', label: 'Project Tasks' });
                seenValues.add('ProjectTask');
            }

            // 2. SIPA items — collapse all SIPA variants (ActionItemsType "SIPA",
            //    templateKey "SipaSubSection", source "SipaReportCapture" /
            //    "SipaReportEnforcement" / "SIPA") into a single "Sipa" entry.
            const isSipa =
                item.actionItemsType === 'SIPA' ||
                item.templateKey === 'SipaSubSection' ||
                item.source === 'SIPA' ||
                item.source === 'SipaReportCapture' ||
                item.source === 'SipaReportEnforcement';
            if (isSipa && !seenValues.has('SIPA')) {
                options.push({ value: 'SIPA', label: 'Sipa' });
                seenValues.add('SIPA');
                // Also mark the individual keys as seen so they don't appear separately
                seenValues.add('SipaSubSection');
                seenValues.add('SipaReportCapture');
                seenValues.add('SipaReportEnforcement');
                continue;
            }
            if (isSipa) continue; // already added the combined entry

            // 3. Template-based items — use the template displayName when available
            if (item.templateKey && !seenValues.has(item.templateKey)) {
                const label = templateLookup[item.templateKey] || formatSourceLabel(item.templateKey);
                options.push({ value: item.templateKey, label });
                seenValues.add(item.templateKey);
            }

            // 3. Source-based items not yet covered by a templateKey match.
            //    Only add a source option when the item does NOT already have a
            //    templateKey entry in the option list.  Without this guard a single
            //    action item contributes two separate filter entries, e.g.:
            //      templateKey "CMDailyChecklist"   → "Daily Centre Management Checklist"
            //      source      "CMDailyChecklistForm" → "CM Daily Checklist Form"  (duplicate)
            //    and:
            //      templateKey "IT-NetworkMonitoring" → "IT Network Monitoring"
            //      source      "RouterMonitoring"      → "Router Monitoring"       (duplicate)
            //    Items that carry NO templateKey (e.g. legacy records) still emit a
            //    source entry because the condition `item.templateKey && ...` is false.
            const sourceAlreadyCoveredByTemplate = item.templateKey && seenValues.has(item.templateKey);
            if (item.source && !seenValues.has(item.source) && !sourceAlreadyCoveredByTemplate) {
                options.push({ value: item.source, label: formatSourceLabel(item.source) });
                seenValues.add(item.source);
            }
        }

        // Sort alphabetically by label so the list is consistent regardless of
        // the order items were inserted
        return options.sort((a, b) => a.label.localeCompare(b.label));
    }, [actionItems, templates]);

    // Filters are now applied server-side via the /by-column endpoint.
    // This memo is kept as a thin wrapper so downstream code (sortedItems,
    // list view, export, select-all, etc.) continues to work unchanged.
    // The only client-side filter still needed is the search highlight matching
    // for the source filter options dropdown (which derives from actionItems).
    const filteredItems = useMemo(() => actionItems, [actionItems]);

    // Sorting is now applied server-side. Keep the memo as a pass-through
    // so all downstream consumers (itemsByStatus, list view, swimlanes) work unchanged.
    const sortedItems = useMemo(() => filteredItems, [filteredItems]);
    /* eslint-enable react-hooks/preserve-manual-memoization */

    // Derive displayed columns from board columns (if configured), otherwise fall back to hardcoded STATUSES.
    // Each entry carries both a display `name` (custom column name) and `statusValue` (for item grouping).
    const configuredColumns = boardColumns.length > 0
        ? boardColumns.sort((a, b) => a.sortOrder - b.sortOrder).map(c => ({
            key: c.columnId || c.name,
            name: c.name,
            statusValue: c.statusValue,
            color: c.color
        }))
        : STATUSES.map(s => ({ key: s, name: s, statusValue: s }));

    // Ensure items with statuses not configured as board columns are still visible.
    // Append any "extra" statuses that have items but no matching column.
    // Insert extras BEFORE any Closed/completed columns so Closed always stays last.
    const configuredStatusValues = [...new Set(configuredColumns.map(c => c.statusValue))];
    const extraStatuses = [...new Set(sortedItems.map(item => item.status))]
        .filter(s => s && !configuredStatusValues.includes(s));

    // Split configured columns into non-closed and closed groups,
    // then sandwich extras between them so Closed is always rightmost.
    const nonClosedConfigured = configuredColumns.filter(c => c.statusValue !== 'Closed');
    const closedConfigured = configuredColumns.filter(c => c.statusValue === 'Closed');
    const boardColumns_display = [
        ...nonClosedConfigured,
        ...extraStatuses.map(s => ({ key: s, name: s, statusValue: s })),
        ...closedConfigured
    ];
    const activeBoardColumns_display = boardColumns_display.filter(c => c.statusValue !== 'Closed');

    // Groups filtered action items by their status for easy rendering in Kanban columns.
    // For columns sharing the same statusValue, each column shows all items with that status.
    const itemsByStatus = boardColumns_display.reduce((acc, col) => {
        acc[col.key] = sortedItems.filter(item => item.status === col.statusValue);
        return acc;
    }, {});

    /**
     * Helper function to get user display name from user object.
     * Handles various user object formats and provides consistent fallback.
     * The /api/User/all endpoint returns { id, name, email } format.
     * @param {Object} userObj - User object from API
     * @returns {string} Display name for the user
     */
    const getUserDisplayName = (userObj) => {
        if (!userObj) return null;
        // Handle /api/User/all format: { id, name, email }
        if (userObj.name) {
            return userObj.name;
        }
        // Handle alternative formats with firstName/lastName
        const first = userObj.firstName || '';
        const last = userObj.lastName || userObj.surname || '';
        const composed = `${first} ${last}`.trim();
        // Return composed name if available, otherwise try fallbacks
        return composed || userObj.displayName || userObj.email || null;
    };

    /**
     * Helper function to format date in "dd MMMM YYYY" format for cards.
     * @param {string|Date} dateValue - Date value to format
     * @returns {string} Formatted date string
     */
    const formatCardDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        try {
            return new Date(dateValue).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });
        } catch (catchError) {
            Sentry.captureException(catchError);
            return 'N/A';
        }
    };

    // Derived boolean for backward compatibility with list view checks
    const isListView = viewMode === 'list';

    return (
        <>
            {showModal && (
                <CreateActionItemModal
                    onClose={() => setShowModal(false)}
                    onCreated={fetchAllColumns}
                    boardCreationTypes={boardCreationTypes}
                    loadingCreationTypes={loadingCreationTypes}
                    selectedCreationType={selectedCreationType}
                    properties={properties}
                    allUsers={allUsers}
                    departments={departments}
                    functions={functions}
                    itCategories={itCategories}
                    getUserDisplayName={getUserDisplayName}
                    selectedBoardId={selectedBoardId}
                />
            )}

            {/* DndContext wraps sidebar, BOARDS tab, and main content for unified drag-and-drop */}
            <DndContext
                sensors={sensors}
                // Use pointerWithin (pointer-inside-rect) with a rectIntersection
                // fallback. closestCenter misbehaves with a DragOverlay floating
                // near the cursor: it picks whichever droppable center is closest
                // in absolute distance, so Kanban column centers regularly beat
                // narrow BoardSidebar items and the hover-switch never fires.
                // pointerWithin fires reliably whenever the cursor is physically
                // over a droppable; rectIntersection catches edge cases where
                // the pointer sits in a gap between siblings.
                collisionDetection={(args) => {
                    const pointerCollisions = pointerWithin(args);
                    if (pointerCollisions.length > 0) return pointerCollisions;
                    return rectIntersection(args);
                }}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
            {/* Board Sidebar */}
            <BoardSidebar
                isOpen={showBoardSidebar}
                onClose={() => setShowBoardSidebar(false)}
                boards={boards}
                selectedBoardId={selectedBoardId}
                onBoardChange={(boardId) => setSelectedBoardId(boardId)}
                onCreateBoard={() => setShowCreateBoardModal(true)}
                onBoardSettings={(board) => {
                    setCurrentBoard(board);
                    setShowBoardSettingsModal(true);
                }}
                onManageAccess={async (board) => {
                    try {
                        const boardDetails = await kanbanBoardService.fetchBoard(apiFetch, board.boardId);
                        setCurrentBoard(boardDetails);
                    } catch (error) {
                        Sentry.captureException(error);
                        console.error('Failed to load board access details:', error);
                        setCurrentBoard(board);
                    }
                    setShowManageAccessModal(true);
                }}
                onDuplicateBoard={(board) => {
                    setCurrentBoard(board);
                    setShowDuplicateBoardModal(true);
                }}
                onDeleteBoard={handleDeleteBoard}
                draggedItemId={draggedItemId}
                previewBoardId={previewBoardId}
                onBoardHover={handleBoardHover}
            />

            {/* Toggle Button for Sidebar — rendered as a BoardsTabButton so that
                useDroppable() is correctly called inside the DndContext provider tree */}
            <BoardsTabButton
                showBoardSidebar={showBoardSidebar}
                onToggle={() => setShowBoardSidebar(!showBoardSidebar)}
            />
            
            <div className={`action-items-page ${showBoardSidebar ? 'sidebar-open' : ''}`}>
                {/* Header Section */}
                <div className="action-items-header">
                    <div className="header-left">
                        <h1>
                            {boards.find(b => b.boardId === selectedBoardId)?.name || 'Action Items'}: Action Items
                        </h1>
                    </div>
                        <div className="header-actions">
                        <button 
                            type="button" 
                            className="btn-primary"
                            onClick={() => setShowModal(true)}
                        >
                            + Create Action Item
                        </button>
                    </div>
                </div>
                
                {/* Search and Filter Bar */}
                <SearchAndFilterBar
                    searchQuery={searchQuery}
                    onSearchChange={(e) => setSearchQuery(e.target.value)}
                    showFilters={showFilters}
                    onToggleFilters={() => setShowFilters(f => !f)}
                    onExport={handleExportToExcel}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    isAdmin={IsAdmin}
                    selectedItemsCount={selectedItems.size}
                    onOpenMergeModal={handleOpenMergeModal}
                    swimGroupBy={swimGroupBy}
                    onSwimGroupByChange={setSwimGroupBy}
                    sortField={sortField}
                    sortDirection={sortDirection}
                />
                
                {/* Filter Panel */}
                {showFilters && (
                    <FilterPanel
                        filterSource={filterSource}
                        onFilterSourceChange={(e) => setFilterSource(e.target.value)}
                        filterStatus={filterStatus}
                        onFilterStatusChange={(e) => setFilterStatus(e.target.value)}
                        filterPriority={filterPriority}
                        onFilterPriorityChange={(e) => setFilterPriority(e.target.value)}
                        filterProperty={filterProperty}
                        onFilterPropertyChange={(e) => setFilterProperty(e.target.value)}
                        filterAssignee={filterAssignee}
                        onFilterAssigneeChange={(e) => setFilterAssignee(e.target.value)}
                        filterCreator={filterCreator}
                        onFilterCreatorChange={(e) => setFilterCreator(e.target.value)}
                        showClosedItems={showClosedItems}
                        onShowClosedItemsChange={(e) => setShowClosedItems(e.target.checked)}
                        onClearFilters={handleClearFilters}
                        availableSourceOptions={availableSourceOptions}
                        propertiesMDA={filterPropertiesByAccess(propertiesMDA, 'propertyID')}
                        allUsers={allUsers}
                        getUserDisplayName={getUserDisplayName}
                        filteredCount={sortedItems.length}
                        totalCount={Object.values(columnMeta).reduce((sum, m) => sum + (m.totalCount || 0), 0)}
                        filterDatePreset={filterDatePreset}
                        onFilterDatePresetChange={(value) => setFilterDatePreset(value)}
                        filterDateFrom={filterDateFrom}
                        onFilterDateFromChange={(value) => setFilterDateFrom(value)}
                        filterDateTo={filterDateTo}
                        onFilterDateToChange={(value) => setFilterDateTo(value)}
                    />
                    )}

                {/* Suggested Merges Banner — shows duplicate detection for IT tickets */}
                    {boards.find(b => b.boardId === selectedBoardId)?.enableDuplicateDetection && (
                        <SuggestedMergesBanner
                            onMergeComplete={fetchAllColumns}
                            boardId={selectedBoardId}
                        />
                    )}

                {/* Main Content - List, Function Swimlanes, Routine Checks Swimlanes, or Regular Kanban View */}
                {isListView ? (
                        <ActionItemsListView
                            filteredItems={sortedItems}
                            selectedItems={selectedItems}
                            onToggleSelection={handleToggleSelection}
                            onSelectAll={handleSelectAll}
                            onClearSelections={handleClearSelections}
                            onOpenMergeModal={handleOpenMergeModal}
                            onAssigneeChange={handleAssigneeChange}
                            onStatusChange={handleStatusChange}
                            onPriorityChange={handlePriorityChange}
                            navigate={navigate}
                            propertiesMDA={propertiesMDA}
                            allUsers={allUsers}
                            needsMyAttention={needsMyAttention}
                            getItemIcon={getItemIcon}
                            getItemDisplayText={getItemDisplayText}
                            getUserDisplayName={getUserDisplayName}
                            getProjectName={getProjectName}
                            isAssignedToMyDepartment={isAssignedToMyDepartment}
                            isAdmin={IsAdmin}
                            onDeleteItem={handleDeleteItem}
                            columnMeta={columnMeta}
                            onLoadMore={loadMoreColumnItems}
                            showClosedItems={showClosedItems}
                        />
                ) : viewMode === 'swimlanes' ? (
                    /* When group-by is 'function' (default), use the board/function swimlanes.
                       For property/assignee/type, use the generic GroupBySwimlanes. */
                    swimGroupBy === 'function' ? (
                        <RoutineChecksSwimlanes
                            boardId={selectedBoardId}
                            items={sortedItems}
                            statuses={STATUSES}
                            activeStatuses={ACTIVE_STATUSES}
                            showClosedItems={showClosedItems}
                            closedItemsLimit={closedItemsLimit}
                            onLoadMoreClosed={() => setClosedItemsLimit(prev => prev + CLOSED_ITEMS_PAGE_SIZE)}
                            propertiesMDA={propertiesMDA}
                            navigate={navigate}
                            needsMyAttention={needsMyAttention}
                            getItemIcon={getItemIcon}
                            getItemDisplayText={getItemDisplayText}
                            getUserDisplayName={getUserDisplayName}
                            formatCardDate={formatCardDate}
                            getProjectName={getProjectName}
                            allUsers={allUsers}
                            isAssignedToMyDepartment={isAssignedToMyDepartment}
                            userID={userID}
                            apiFetch={apiFetch}
                        />
                    ) : (
                        <GroupBySwimlanes
                            groupBy={swimGroupBy}
                            items={sortedItems}
                            statuses={STATUSES}
                            activeStatuses={ACTIVE_STATUSES}
                            showClosedItems={showClosedItems}
                            closedItemsLimit={closedItemsLimit}
                            onLoadMoreClosed={() => setClosedItemsLimit(prev => prev + CLOSED_ITEMS_PAGE_SIZE)}
                            propertiesMDA={propertiesMDA}
                            navigate={navigate}
                            needsMyAttention={needsMyAttention}
                            getItemIcon={getItemIcon}
                            getItemDisplayText={getItemDisplayText}
                            getUserDisplayName={getUserDisplayName}
                            formatCardDate={formatCardDate}
                            getProjectName={getProjectName}
                            allUsers={allUsers}
                            isAssignedToMyDepartment={isAssignedToMyDepartment}
                        />
                    )
                ) : (
                        <div className="kanban-board-wrapper">
                            {/* Sticky status label row — lives outside the overflow-x container
                                so position:sticky works relative to the page (top: navbar height).
                                The .kanban-board below scrolls horizontally without breaking sticky. */}
                                    <div className="kanban-status-headers">
                                        {(showClosedItems ? boardColumns_display : activeBoardColumns_display).map(col => {
                                            const colCount = columnMeta[col.statusValue]?.totalCount
                                                ?? (itemsByStatus[col.key]?.length ?? 0);
                                            return (
                                                <div
                                                    key={col.key}
                                                    className={`kanban-status-label status-${col.statusValue.replace(/\s+/g, '').replace(/-/g, '').toLowerCase()}`}
                                                    style={col.color ? { background: col.color } : undefined}
                                                >
                                                    {col.name}
                                                    <span className="kanban-col-count">({colCount})</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="kanban-board">
                                        {(showClosedItems ? boardColumns_display : activeBoardColumns_display).map(col => {
                                            const columnItems = itemsByStatus[col.key] || [];
                                            const meta = columnMeta[col.statusValue] || {};
                                            const totalForColumn = meta.totalCount || columnItems.length;
                                            const hasMoreItems = meta.hasMore || false;

                                            return (
                                                <KanbanColumn
                                                    key={col.key}
                                                    status={col.statusValue}
                                                    displayName={col.name}
                                                    color={col.color}
                                                    items={columnItems}
                                                    propertiesMDA={propertiesMDA}
                                                    navigate={navigate}
                                                    hasMore={hasMoreItems}
                                                    totalItems={totalForColumn}
                                                    onLoadMore={() => loadMoreColumnItems(col.statusValue)}
                                                    needsMyAttention={needsMyAttention}
                                                    getItemIcon={getItemIcon}
                                                    getItemDisplayText={getItemDisplayText}
                                                    getUserDisplayName={getUserDisplayName}
                                                    formatCardDate={formatCardDate}
                                                    getProjectName={getProjectName}
                                                    allUsers={allUsers}
                                                    isAssignedToMyDepartment={isAssignedToMyDepartment}
                                                    hideHeader={true}
                                                />
                                            );
                                        })}
                                    </div>
                        </div>
                )}
</div>

            {/* DragOverlay renders the dragged card in a portal outside the board
                DOM, so switching boards mid-drag (via handleBoardHover) does not
                unmount the source node and leave the pointer with nothing attached.
                Uses a lightweight static preview to avoid re-registering the same
                draggable id inside the overlay tree. */}
            <DragOverlay dropAnimation={null}>
                {draggedItemId ? (() => {
                    // Prefer the snapshot taken at drag-start — the live
                    // actionItems list may have been swapped by a cross-board
                    // hover, in which case the find() below would return
                    // undefined and the overlay would vanish mid-drag.
                    const dragged = draggedItemSnapshot
                        || actionItems.find(i => i.id.toString() === draggedItemId.toString());
                    if (!dragged) return null;
                    const priorityColors = {
                        'Critical': '#e74c3c',
                        'High': '#e67e22',
                        'Medium': '#f39c12',
                        'Low': '#3498db'
                    };
                    const previewColor = priorityColors[dragged.priority] || '#95a5a6';
                    const titleText = dragged.title || dragged.description || `Action Item #${dragged.id}`;
                    return (
                        <div
                            className="action-item-card compact drag-overlay-preview"
                            style={{
                                borderLeft: `3px solid ${previewColor}`,
                                cursor: 'grabbing',
                                boxShadow: '0 12px 24px rgba(16, 24, 40, 0.18), 0 4px 8px rgba(16, 24, 40, 0.10)',
                                transform: 'rotate(1.5deg)',
                                pointerEvents: 'none',
                                width: 260
                            }}
                        >
                            <div className="card-row card-row-title">
                                <span className="card-title-text">{titleText}</span>
                            </div>
                            <div className="card-row card-row-meta">
                                <span className={`status-chip status-${(dragged.status || '').replace(/\s+/g, '').toLowerCase()}`}>
                                    {dragged.status}
                                </span>
                            </div>
                        </div>
                    );
                })() : null}
            </DragOverlay>
            </DndContext>

            {/* Merge Action Items Modal */}
            <MergeActionItemsModal
                show={showMergeModal}
                onClose={() => {
                    setShowMergeModal(false);
                    setMergeTargetId(null);
                }}
                selectedItems={selectedItems}
                mergeTargetId={mergeTargetId}
                onMergeTargetChange={(e) => setMergeTargetId(parseInt(e.target.value, 10))}
                onMerge={handleMergeItems}
                isMerging={isMerging}
                actionItems={actionItems}
            />
            
            {/* Board Management Modals */}
            {showCreateBoardModal && (
                <CreateBoardModal
                    onClose={() => setShowCreateBoardModal(false)}
                    onCreate={async (boardData) => {
                        try {
                            await kanbanBoardService.createBoard(apiFetch, boardData);
                            await loadBoards();
                            setShowCreateBoardModal(false);
                            toast.success('Board created successfully');
                        } catch (error) {
                            Sentry.captureException(error);
                            console.error('Failed to create board:', error);
                            toast.error('Failed to create board');
                        }
                    }}
                />
            )}
            
            {showBoardSettingsModal && currentBoard && (
                <BoardSettingsModal
                    board={currentBoard}
                    onClose={() => {
                        setShowBoardSettingsModal(false);
                        setCurrentBoard(null);
                    }}
                    onSave={async (boardId, updates) => {
                        try {
                            // Update board metadata (name, description, color, etc.)
                            const { columns, ...boardUpdates } = updates;
                            await kanbanBoardService.updateBoard(apiFetch, boardId, boardUpdates);

                            // Handle column operations if present
                            if (columns && columns.length > 0) {
                                // Get existing columns from the board to determine adds/updates/deletes
                                const boardData = await kanbanBoardService.fetchBoard(apiFetch, boardId);
                                const existingColumns = boardData?.columns || [];
                                const existingIds = new Set(existingColumns.map(c => c.columnId));

                                // Add new columns (temp IDs or IDs not in existing)
                                for (const col of columns) {
                                    const colId = col.columnId;
                                    const isNew = !colId || String(colId).startsWith('temp-') || !existingIds.has(colId);
                                    if (isNew) {
                                        await kanbanBoardService.addColumn(apiFetch, boardId, {
                                            name: col.name,
                                            statusValue: col.statusValue,
                                            color: col.color,
                                            sortOrder: col.sortOrder,
                                            allowCreate: col.allowCreate,
                                            isCompletedColumn: col.isCompletedColumn,
                                            isDefaultColumn: col.isDefaultColumn,
                                            wipLimit: col.wipLimit
                                        });
                                    } else {
                                        // Update existing column and capture how many items
                                        // had their Status cascaded due to a StatusValue rename.
                                        const result = await kanbanBoardService.updateColumn(apiFetch, boardId, colId, {
                                            name: col.name,
                                            statusValue: col.statusValue,
                                            color: col.color,
                                            sortOrder: col.sortOrder,
                                            allowCreate: col.allowCreate,
                                            isCompletedColumn: col.isCompletedColumn,
                                            isDefaultColumn: col.isDefaultColumn,
                                            wipLimit: col.wipLimit
                                        });
                                    }
                                }

                                // Delete columns that were removed
                                const updatedIds = new Set(columns.filter(c => c.columnId && !String(c.columnId).startsWith('temp-')).map(c => c.columnId));
                                for (const existing of existingColumns) {
                                    if (!updatedIds.has(existing.columnId)) {
                                        await kanbanBoardService.deleteColumn(apiFetch, boardId, existing.columnId);
                                    }
                                }
                            }

                            setShowBoardSettingsModal(false);
                            setCurrentBoard(null);
                            await loadBoards();
                            await loadBoardColumns();
                            await fetchAllColumns();

                            // Broadcast column changes so other users see them in real-time
                            if (columns && columns.length > 0) {
                                broadcastColumnsChange('updated', { boardId, columns });
                            }
                        } catch (error) {
                            Sentry.captureException(error);
                            console.error('Failed to update board:', error);
                            toast.error(error.message || 'Failed to update board');
                        }
                    }}
                    onDelete={async (boardId) => {
                        try {
                            await kanbanBoardService.deleteBoard(apiFetch, boardId);
                            toast.success('Board deleted successfully');
                            setShowBoardSettingsModal(false);
                            setCurrentBoard(null);
                            await loadBoards();
                            
                            // If deleted board was selected, clear selection
                            if (selectedBoardId === boardId) {
                                setSelectedBoardId(null);
                            }
                        } catch (error) {
                            Sentry.captureException(error);
                            console.error('Failed to delete board:', error);
                            toast.error('Failed to delete board');
                        }
                    }}
                    onAddColumn={async (boardId, columnData) => {
                        try {
                            await kanbanBoardService.addColumn(apiFetch, boardId, columnData);
                            toast.success('Column added successfully');
                            await loadBoards();
                        } catch (error) {
                            Sentry.captureException(error);
                            console.error('Failed to add column:', error);
                            toast.error('Failed to add column');
                        }
                    }}
                    onUpdateColumn={async (boardId, columnId, updates) => {
                        try {
                            await kanbanBoardService.updateColumn(apiFetch, boardId, columnId, updates);
                            toast.success('Column updated successfully');
                            await loadBoards();
                        } catch (error) {
                            Sentry.captureException(error);
                            console.error('Failed to update column:', error);
                            toast.error('Failed to update column');
                        }
                    }}
                    onDeleteColumn={async (boardId, columnId) => {
                        try {
                            await kanbanBoardService.deleteColumn(apiFetch, boardId, columnId);
                            toast.success('Column deleted successfully');
                            await loadBoards();
                        } catch (error) {
                            Sentry.captureException(error);
                            console.error('Failed to delete column:', error);
                            toast.error('Failed to delete column');
                        }
                    }}
                />
            )}
            
            {showManageAccessModal && currentBoard && (
                <ManageAccessModal
                    board={currentBoard}
                    sharedWith={currentBoard.sharedWith || []}
                    onClose={() => {
                        setShowManageAccessModal(false);
                        setCurrentBoard(null);
                    }}
                    onAccessChanged={loadBoards}
                    onGrantAccess={async (accessData) => {
                        try {
                            await kanbanBoardService.grantAccess(apiFetch, currentBoard.boardId, accessData);
                            toast.success('Access granted successfully');
                            await loadBoards();
                        } catch (error) {
                            Sentry.captureException(error);
                            console.error('Failed to grant access:', error);
                            toast.error('Failed to grant access');
                        }
                    }}
                    onRevokeAccess={async (accessId) => {
                        try {
                            await kanbanBoardService.revokeAccess(apiFetch, currentBoard.boardId, accessId);
                            toast.success('Access revoked successfully');
                            await loadBoards();
                        } catch (error) {
                            Sentry.captureException(error);
                            console.error('Failed to revoke access:', error);
                            toast.error('Failed to revoke access');
                        }
                    }}
                />
            )}
            
            {showDuplicateBoardModal && currentBoard && (
                <DuplicateBoardModal
                    board={currentBoard}
                    onClose={() => {
                        setShowDuplicateBoardModal(false);
                        setCurrentBoard(null);
                    }}
                    onSuccess={async () => {
                        await loadBoards();
                    }}
                />
            )}
        </>
    );
};

export default ActionItemsPage;
